import React, {JSX, useEffect, useState} from 'react';
import {BlockBase, ContentData} from '../../interfaces.ts';
import {getContentValue, isBlockTypeCorrect} from '../../utils.ts';
import {ClientSideEditor as LexicalEditor} from '@inscript/lexical-editor';
import {useServerProvider} from "../../use-server-provider.tsx";
import {getHtml} from "@inscript/lexical-editor/server";
import {useActiveEditor} from "@inscript/lexical-editor";
import ContentEditable from "../../components/ContentEditable.tsx";

interface TextBlockProperties {
  withHeader: boolean;
  headerLevel: number;
  placeholder?: string;
}

interface TextBlockContentData {
  content: string;
  headerText: string;
}

const TextBlock: React.FC<{ block: BlockBase<TextBlockProperties, TextBlockContentData>, contentData?: ContentData<TextBlockContentData> | null, editable?: boolean, locale: string, fallbackLocale: string }> = ({ block, contentData, editable = false, locale, fallbackLocale }) => {
  const { id, properties } = block;
  const onContentChange = useServerProvider<(blockId: string, field: string, value: any) => void>('onContentChange')
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { content, headerText } = getContentValue(locale, fallbackLocale, block, contentData)
  const {setActiveEditor} = useActiveEditor();

  useEffect(() => {
    if (!editable && typeof content !== 'string' && content) {
      setLoading(true);
      getHtml(content).then(html => {
        setHtmlContent(html);
        setLoading(false);
      }).catch(error => {
        console.error('Error converting lexical to HTML:', error);
        setLoading(false);
      });
    }
  }, [htmlContent, editable, content]);

  if (!isBlockTypeCorrect(block, 'textBlock')) {
    return null
  }

  const placeholder = properties.placeholder;
  
  if (!content && !headerText && !placeholder) {
    return null;
  }

  const handleInput = (event: string) => {
      if (onContentChange) {
          onContentChange?.value(id, 'headerText', event);
      }
  };

  const tagName = `h${properties.headerLevel}` as keyof JSX.IntrinsicElements;
  const headerBlock = (
    <ContentEditable
      id={id}
      key={id}
      contentEditable={editable}
      className={id}
      onFocus={() => setActiveEditor(id)}
      data-block-id={id}
      data-field="headerText"
      onChange={handleInput}
      value={headerText}
      tagName={tagName}
    />
  );
  let contentBlock: JSX.Element|null = null;

  const lexicalHandleContentChange = (value: any) => {
    if (onContentChange.value) {
      onContentChange.value(id, 'content', value)
    }
  }
  
  if (editable) {
    contentBlock =
      <LexicalEditor
          key={`${id}-${locale}`}
          value={content}
          onChange={
            (value) => lexicalHandleContentChange(value)
          }
          placeholder={placeholder}
      />;
  } else {
    if (typeof content === 'string') {
      contentBlock = <div dangerouslySetInnerHTML={{ __html: content }} data-field="content" />
    } else {
      // Show loading state or rendered HTML
      if (loading) {
        contentBlock = <div>Loading...</div>;
      } else if (htmlContent !== null) {
        contentBlock = <div dangerouslySetInnerHTML={{ __html: htmlContent }} data-field="content" />;
      } else {
        contentBlock = <div className={"editor-placeholder"}>{placeholder || 'No content available'}</div>;
      }
    }
  }

  return (
    <div id={id} className={id} key={id} data-block-id={id}>
      {properties.withHeader && headerBlock}
      {contentBlock}
    </div>
  )
};

export default TextBlock;